import { Link } from "react-router-dom";
import { Linkedin, Twitter, Facebook, Instagram, Youtube } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useLang } from "@/context/LanguageContext";
import { LOGO } from "@/lib/site";
import { RiskWarning, IBDisclosure } from "@/components/common/RiskDisclosures";

const SOCIAL = [
  ["linkedin", Linkedin], ["twitter", Twitter], ["facebook", Facebook], ["instagram", Instagram], ["youtube", Youtube],
];

export function Footer() {
  const { settings } = useSite();
  const { L } = useLang();
  const social = settings.social || {};
  const ln = L.footer.links;

  const COLS = [
    { title: L.footer.cols.company, links: [[ln.about, "/about-us"], [ln.why, "/why-menagroupfx"], [ln.brokerPartners, "/broker-partners"], [ln.contact, "/contact"], [ln.faq, "/faq"]] },
    { title: L.footer.cols.markets, links: [[ln.forex, "/markets/forex"], [ln.commodities, "/markets/commodities"], [ln.indices, "/markets/indices"], [ln.shares, "/markets/shares"]] },
    { title: L.footer.cols.trading, links: [[ln.platforms, "/trading-platforms"], [ln.accounts, "/account-solutions"], [ln.analysis, "/market-analysis"], [ln.calendar, "/economic-calendar"], [ln.education, "/education"]] },
    { title: L.footer.cols.partners, links: [[ln.become, "/become-a-partner"], [ln.ib, "/partners"], [ln.login, "/admin/login"]] },
    { title: L.footer.cols.legal, links: [[ln.risk, "/legal/risk-disclosure"], [ln.privacy, "/legal/privacy-policy"], [ln.terms, "/legal/terms-and-conditions"], [ln.cookies, "/legal/cookie-policy"], [ln.complaints, "/legal/complaints-policy"], [ln.regulatory, "/legal/regulatory-information"]] },
  ];

  return (
    <footer className="bg-navy text-slate-300" data-testid="site-footer">
      <div className="container-tight py-16">
        <div className="grid gap-10 lg:grid-cols-6">
          <div className="lg:col-span-1">
            <Link to="/" className="inline-flex">
              <img src={LOGO} alt="MENAGroupFX" className="h-11 w-auto brightness-0 invert" />
            </Link>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">{L.footer.desc}</p>
            <div className="mt-5 flex gap-2">
              {SOCIAL.map(([key, Icon]) => (
                <a
                  key={key}
                  href={social[key] || "#"}
                  target="_blank"
                  rel="noreferrer"
                  className={`flex h-9 w-9 items-center justify-center rounded-md border border-white/10 transition-colors hover:border-gold hover:text-gold ${social[key] ? "" : "pointer-events-none opacity-40"}`}
                  data-testid={`footer-social-${key}`}
                  aria-label={key}
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {COLS.map((col) => (
            <div key={col.title}>
              <h4 className="font-heading text-sm font-semibold uppercase tracking-wide text-white">{col.title}</h4>
              <ul className="mt-4 space-y-2.5">
                {col.links.map(([label, to]) => (
                  <li key={to}><Link to={to} className="text-sm text-slate-400 transition-colors hover:text-gold">{label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 space-y-4 border-t border-white/10 pt-8">
          <IBDisclosure />
          <RiskWarning />
        </div>

        <div className="mt-8 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-6 text-xs text-slate-500 sm:flex-row">
          <p>© {new Date().getFullYear()} MENAGroupFX. {L.footer.rights}</p>
          <p>{settings.email} {settings.phone ? `· ${settings.phone}` : ""}</p>
        </div>
      </div>
    </footer>
  );
}
